"use client";
import { getDataAutomationGroup } from "@/helpers";
import {
  Blocks,
  BuilderBlock,
  BuilderContextInterface,
  isEditing,
  isPreviewing,
  RegisteredComponents,
} from "@builder.io/sdk-react";
import cn from "classnames";
import {
  FC,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

const TOOLTIP_OPEN_EVENT = "tooltip:open";
const MARGIN = 8;
const GAP = 10;
const MAX_WIDTH = 320;
// never squeeze a left/right tooltip below this before flipping axis
const MIN_SIDE_WIDTH = 120;

type Position = "top" | "right" | "bottom" | "left";

type TooltipBlockProps = {
  position: Position;
  content: BuilderBlock[];
  tooltipText: BuilderBlock[];
  builderBlock: BuilderBlock;
  builderComponents: RegisteredComponents;
  builderLinkComponent: unknown;
  builderContext: BuilderContextInterface;
};

export const TooltipBlock: FC<TooltipBlockProps> = ({
  content,
  tooltipText,
  position,
  builderBlock,
  builderComponents,
  builderLinkComponent,
  builderContext,
}) => {
  const builderId = builderBlock?.id;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const lockedByClickRef = useRef<boolean>(false);
  const [lockedByClick, setLockedByClick] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
    maxWidth: number;
    resolvedPosition: Position;
  }>({ top: 0, left: 0, maxWidth: 320, resolvedPosition: position });

  // Helper to apply SVG swap based on lock state
  const applyIconState = useCallback((locked: boolean) => {
    if (!wrapperRef.current) return;
    const icon = wrapperRef.current.querySelector<HTMLElement>(".tooltipIcon");
    const pressed = wrapperRef.current.querySelector<HTMLElement>(
      ".tooltipIconPressed",
    );
    if (icon) icon.style.display = locked ? "none" : "block";
    if (pressed) pressed.style.display = locked ? "block" : "none";
  }, []);

  useEffect(() => {
    applyIconState(lockedByClick);
  }, [lockedByClick, applyIconState]);

  useEffect(() => {
    if (!lockedByClick || !wrapperRef.current) return;

    let frame = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        applyIconState(lockedByClickRef.current),
      );
    });
    observer.observe(wrapperRef.current, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [lockedByClick, applyIconState]);

  const closeTooltip = useCallback(() => {
    lockedByClickRef.current = false;
    setLockedByClick(false);
    setIsTooltipVisible(false);
  }, []);

  const openTooltip = useCallback(() => {
    document.dispatchEvent(
      new CustomEvent(TOOLTIP_OPEN_EVENT, { detail: { id: builderId } }),
    );
    setIsTooltipVisible(true);
  }, [builderId]);

  const calculateTooltipPosition = useCallback(() => {
    const wrapper = wrapperRef.current;
    const el = tooltipRef.current;
    if (!wrapper || !el) return;

    const rect = wrapper.getBoundingClientRect();
    const { innerWidth: vw, innerHeight: vh } = window;

    const spaceTop = rect.top - GAP - MARGIN;
    const spaceBottom = vh - rect.bottom - GAP - MARGIN;
    const spaceLeft = rect.left - GAP - MARGIN;
    const spaceRight = vw - rect.right - GAP - MARGIN;

    const viewportMaxWidth = Math.min(MAX_WIDTH, vw - 2 * MARGIN);
    el.style.maxWidth = `${viewportMaxWidth}px`;

    let tip = el.getBoundingClientRect();
    let { width: w, height: h } = tip;

    let resolvedPosition = position;

    if (position === "top" || position === "bottom") {
      if (position === "top" && spaceTop < h && spaceBottom > spaceTop) {
        resolvedPosition = "bottom";
      } else if (
        position === "bottom" &&
        spaceBottom < h &&
        spaceTop > spaceBottom
      ) {
        resolvedPosition = "top";
      }
    } else {
      const fitsLeft = spaceLeft >= Math.min(w, MIN_SIDE_WIDTH);
      const fitsRight = spaceRight >= Math.min(w, MIN_SIDE_WIDTH);

      if (position === "left" && !fitsLeft && fitsRight) {
        resolvedPosition = "right";
      } else if (position === "right" && !fitsRight && fitsLeft) {
        resolvedPosition = "left";
      }

      if (!fitsLeft && !fitsRight) {
        resolvedPosition = spaceBottom >= spaceTop ? "bottom" : "top";
      }
    }

    let effectiveMaxWidth = viewportMaxWidth;
    if (resolvedPosition === "left") {
      effectiveMaxWidth = Math.min(
        viewportMaxWidth,
        Math.max(spaceLeft, MIN_SIDE_WIDTH),
      );
    } else if (resolvedPosition === "right") {
      effectiveMaxWidth = Math.min(
        viewportMaxWidth,
        Math.max(spaceRight, MIN_SIDE_WIDTH),
      );
    }
    el.style.maxWidth = `${effectiveMaxWidth}px`;

    tip = el.getBoundingClientRect();
    ({ width: w, height: h } = tip);

    // Anchor point next to the trigger.
    const anchors = {
      top: { top: rect.top - GAP, left: rect.left + rect.width / 2 },
      bottom: { top: rect.bottom + GAP, left: rect.left + rect.width / 2 },
      left: { top: rect.top + rect.height / 2, left: rect.left - GAP },
      right: { top: rect.top + rect.height / 2, left: rect.right + GAP },
    };
    let { top, left } = anchors[resolvedPosition];

    // Clamp within viewport, accounting for CSS transform offsets.
    const isVertical =
      resolvedPosition === "top" || resolvedPosition === "bottom";
    if (isVertical) {
      left = Math.max(w / 2 + MARGIN, Math.min(left, vw - w / 2 - MARGIN));
      top =
        resolvedPosition === "top"
          ? Math.max(h + MARGIN, Math.min(top, vh - MARGIN))
          : Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
    } else {
      top = Math.max(h / 2 + MARGIN, Math.min(top, vh - h / 2 - MARGIN));
      left =
        resolvedPosition === "left"
          ? Math.max(w + MARGIN, Math.min(left, vw - MARGIN))
          : Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
    }
    const bodyRect = document.body.getBoundingClientRect();
    setTooltipPosition({
      top: top - bodyRect.top,
      left: left - bodyRect.left,
      maxWidth: effectiveMaxWidth,
      resolvedPosition,
    });
  }, [position]);

  useEffect(() => {
    if (!isTooltipVisible || !tooltipRef.current) return;

    const ro = new ResizeObserver(() => calculateTooltipPosition());
    ro.observe(tooltipRef.current);

    return () => ro.disconnect();
  }, [isTooltipVisible, calculateTooltipPosition]);

  useEffect(() => {
    if (!isTooltipVisible) return;

    const handleResize = () => {
      calculateTooltipPosition();
    };

    window.addEventListener("resize", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize, true);
    };
  }, [isTooltipVisible, calculateTooltipPosition]);

  useEffect(() => {
    if (!isTooltipVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const tooltipElement = document.getElementById(`${builderId}-tooltip`);

      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        tooltipElement &&
        !tooltipElement.contains(target)
      ) {
        closeTooltip();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTooltipVisible, builderId, closeTooltip]);

  useEffect(() => {
    const handleOtherTooltipOpen = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const otherId = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (otherId !== builderId) {
        closeTooltip();
      }
    };

    document.addEventListener(TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen);

    return () => {
      document.removeEventListener(TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen);
    };
  }, [builderId, closeTooltip]);

  const handleTooltipVisibility = (visible: boolean) => {
    if (visible) {
      openTooltip();
    } else if (!lockedByClickRef.current) {
      setIsTooltipVisible(false);
    }
  };

  const handleClick = () => {
    if (lockedByClickRef.current) {
      closeTooltip();
      return;
    }
    lockedByClickRef.current = true;
    setLockedByClick(true);
    applyIconState(true);
    openTooltip();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };
  const ContentBlock = (
    <Blocks
      blocks={content}
      parent={builderId}
      registeredComponents={builderComponents}
      linkComponent={builderLinkComponent}
      context={builderContext}
      path="content"
    />
  );
  const TooltipTextBlock = (
    <Blocks
      blocks={tooltipText}
      parent={builderId}
      registeredComponents={builderComponents}
      linkComponent={builderLinkComponent}
      context={builderContext}
      path="tooltipText"
    />
  );

  if (isEditing() || isPreviewing()) {
    return (
      <div className="flex w-fit flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="border-b border-black font-bold">Content:</span>
          <span>{ContentBlock}</span>
        </div>
        <div className="flex flex-col gap-2">
          <span className="border-b border-black font-bold">Tooltip Text:</span>
          <span>{TooltipTextBlock}</span>
        </div>
      </div>
    );
  }

  const TooltipContent = (
    <div
      ref={tooltipRef}
      {...getDataAutomationGroup("tooltip")}
      id={`${builderId}-tooltip`}
      role="tooltip"
      data-tooltip-position={tooltipPosition.resolvedPosition}
      className={cn(
        "absolute z-10 w-max",
        !isTooltipVisible && "hidden",
        !lockedByClick && "pointer-events-none",
      )}
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        maxWidth: `${tooltipPosition.maxWidth}px`,
        transform:
          tooltipPosition.resolvedPosition === "top"
            ? "translate(-50%, -100%)"
            : tooltipPosition.resolvedPosition === "bottom"
              ? "translateX(-50%)"
              : tooltipPosition.resolvedPosition === "left"
                ? "translate(-100%, -50%)"
                : "translateY(-50%)",
      }}
    >
      {TooltipTextBlock}
    </div>
  );

  return (
    <>
      <div
        ref={wrapperRef}
        className="relative inline-flex w-fit cursor-pointer items-center select-none"
        style={{ marginInlineStart: "var(--spacing-2, 0.5rem)" }}
        tabIndex={0}
        role="button"
        aria-label="tooltip"
        aria-expanded={isTooltipVisible}
        aria-describedby={`${builderId}-tooltip`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => handleTooltipVisibility(true)}
        onMouseLeave={() => handleTooltipVisibility(false)}
        onFocus={() => handleTooltipVisibility(true)}
        onBlur={(e) => {
          const tooltip = document.getElementById(`${builderId}-tooltip`);
          if (tooltip?.contains(e.relatedTarget as Node)) return;
          handleTooltipVisibility(false);
        }}
      >
        {ContentBlock}
      </div>
      {typeof document !== "undefined" &&
        createPortal(TooltipContent, document.body)}
    </>
  );
};
